import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  useFloating, 
  autoUpdate, 
  offset, 
  flip, 
  shift, 
  size,
  FloatingPortal
} from '@floating-ui/react';

interface Option {
  id: string;
  label: string;
  subtitle?: string;
}

interface SearchDropdownProps {
  options: Option[];
  onSelect: (option: Option) => void;
  placeholder?: string;
  isLoading?: boolean;
  onSearch?: (query: string) => void;
  error?: string;
  className?: string;
  inputClassName?: string;
  resetOnSelect?: boolean;
  enableArrowNavigation?: boolean;
  disabled?: boolean;
  value?: Option | null;
  isTaskDropdown?: boolean; // Special handling for task dropdown
  zIndex?: number; // Custom z-index for dropdown
  preferAbove?: boolean; // Prefer positioning above the input
  dropdownSize?: 'sm' | 'md'; // Compact (default) or standard sizing for dropdown items
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  options,
  onSelect,
  placeholder = "Search...",
  isLoading = false,
  onSearch,
  error,
  className = "",
  inputClassName = "",
  resetOnSelect = false,
  enableArrowNavigation = false,
  disabled = false,
  value = null,
  isTaskDropdown = false,
  zIndex = 9999,
  preferAbove = false,
  dropdownSize = 'sm'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(value?.label || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Floating UI setup
  const { refs, floatingStyles } = useFloating({
    open: isOpen && !disabled,
    onOpenChange: (open) => !disabled && setIsOpen(open),
    placement: preferAbove ? 'top-start' : 'bottom-start',
    middleware: [
      offset(4),
      flip({
        fallbackPlacements: preferAbove ? ['bottom-start', 'top-start'] : ['top-start', 'bottom-start'],
        padding: 10
      }),
      shift({ padding: 10 }),
      size({
        apply({ rects, elements }) {
          const refWidth = rects.reference.width;
          Object.assign(elements.floating.style, {
            width: `${refWidth}px`,
            maxHeight: '320px'
          });
        },
        padding: 10
      })
    ],
    whileElementsMounted: autoUpdate
  });


  // Use useMemo for filtering to avoid unnecessary re-renders
  const filteredOptions = React.useMemo(() => {
    if (!onSearch) {
      // Only filter locally if not using external search
      return options.filter(option =>
        (option.label && option.label.toLowerCase().includes(searchValue.toLowerCase())) ||
        (option.id && option.id.toLowerCase().includes(searchValue.toLowerCase()))
      );
    } else {
      // If using external search, just use the options as-is
      return options;
    }
  }, [options, searchValue, onSearch]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length]);

  // Sync value prop with searchValue
  useEffect(() => {
    const newLabel = value?.label || '';
    setSearchValue(prev => prev !== newLabel ? newLabel : prev);
  }, [value]);

  // Force close dropdown when disabled
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both input and dropdown
      const referenceEl = refs.reference.current as HTMLElement | null;
      const floatingEl = refs.floating.current as HTMLElement | null;
      
      if (referenceEl && !referenceEl.contains(target) &&
          floatingEl && !floatingEl.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, refs.reference, refs.floating]);

  // Debounced search function
  const debouncedSearch = useCallback((value: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      // For task dropdown, require 3+ characters, for others 2+ characters
      const minLength = isTaskDropdown ? 3 : 2;
      if (onSearch && value.length >= minLength) {
        onSearch(value);
      }
    }, 300); // 300ms debounce
  }, [onSearch, isTaskDropdown]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const value = e.target.value;
    setSearchValue(value);
    
    // For task dropdown, only open if we have enough characters
    // For other dropdowns, always open when typing or when empty (to show all values)
    if (isTaskDropdown) {
      const minLength = 3;
      setIsOpen(value.length >= minLength);
    } else {
      setIsOpen(true); // Always open for non-task dropdowns
    }
    
    setHighlightedIndex(-1); // Reset highlight when typing
    
    // No need to update position - Floating UI handles it automatically
    
    debouncedSearch(value);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || !enableArrowNavigation || !isOpen || filteredOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleOptionSelect = (option: Option) => {
    if (disabled) return;
    
    onSelect(option);
    setSearchValue(resetOnSelect ? '' : option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle clearing/deleting selected value
  const handleClearValue = () => {
    if (disabled) return;
    
    setSearchValue('');
    setIsOpen(true); // Show all values when cleared
    setHighlightedIndex(-1);
    
    // Notify parent component that selection is cleared
    onSelect(null as any);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          value={searchValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (!disabled) {
              // For task dropdown, only open if there's already content or show nothing
              // For other dropdowns, always show all values on focus
              if (isTaskDropdown) {
                const minLength = 3;
                setIsOpen(searchValue.length >= minLength);
              } else {
                setIsOpen(true);
              }
            }
          }}
          ref={refs.setReference}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 3xl:px-4 py-2 3xl:py-2.5 border rounded-lg transition-all duration-200 text-[11px] 2xl:text-xs 3xl:text-sm text-slate-900 placeholder:text-[10px] 2xl:placeholder:text-[11px] 3xl:placeholder:text-xs ${
            error ? 'border-red-300 bg-red-50' : 'border-slate-300 bg-white'
          } ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed opacity-60' : 'hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300'} ${inputClassName}`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 3xl:pr-4">
          {isLoading ? (
            <svg className="w-4 h-4 3xl:w-5 3xl:h-5 animate-spin text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : value && !disabled ? (
            // Show clear button when item is selected and not disabled
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearValue();
              }}
              className="w-4 h-4 3xl:w-5 3xl:h-5 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            // Show search icon when no item is selected
            <svg className="w-4 h-4 3xl:w-5 3xl:h-5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {isOpen && !disabled && (
        <FloatingPortal>
          <div 
            ref={refs.setFloating}
            className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-y-auto"
            style={{
              ...floatingStyles,
              zIndex: zIndex
            }}
          >
          {filteredOptions.length === 0 ? (
            searchValue.length >= (isTaskDropdown ? 3 : 2) && (
              <div className="px-3 3xl:px-4 py-3 3xl:py-4 text-center">
                <div className="text-slate-400 mb-1">
                  {isLoading ? (
                    <svg className="w-5 h-5 animate-spin mx-auto" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>
                <p className={`${dropdownSize === 'md' ? 'text-sm' : 'text-[11px] 2xl:text-xs 3xl:text-sm'} text-slate-600`}>
                  {isLoading ? 'Searching...' : 'No results found'}
                </p>
              </div>
            )
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={option.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input from losing focus
                }}
                className={`w-full ${dropdownSize === 'md' ? 'px-4 py-2.5' : 'px-3 3xl:px-4 py-2 3xl:py-2.5'} text-left focus:outline-none transition-colors duration-150 border-b border-slate-100 last:border-b-0 ${
                  highlightedIndex === index
                    ? 'bg-indigo-50 text-indigo-900'
                    : 'hover:bg-slate-50 focus:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`${dropdownSize === 'md' ? 'text-sm' : 'text-[11px] 2xl:text-xs 3xl:text-sm'} font-normal text-slate-900 flex-1`}>{option.label}</div>
                  {option.subtitle && (
                    <div className={`${dropdownSize === 'md' ? 'text-xs' : 'text-[9px] 2xl:text-[10px] 3xl:text-xs'} text-slate-500 ml-2 flex-shrink-0`}>({option.subtitle})</div>
                  )}
                </div>
              </button>
            ))
          )}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};
